/**
 * Supabase utility functions for IntentDrive.ai
 * Provides safe access to Supabase URL and key
 * Works in both main app and embedded widget contexts
 */

/**
 * Gets Supabase URL - works in both main app and widget contexts
 */
export const getSupabaseUrl = (): string => {
  // Try environment variable first
  const envUrl = import.meta.env.VITE_SUPABASE_URL;
  if (envUrl) return envUrl;

  // Try window global (for runtime injection in widgets)
  if (typeof window !== 'undefined' && (window as any).__INTENTDRIVE_SUPABASE_URL__) {
    return (window as any).__INTENTDRIVE_SUPABASE_URL__;
  }

  // Fallback to client instance (for main app)
  try {
    const { supabase } = require('@/integrations/supabase/client');
    return supabase.supabaseUrl;
  } catch {
    throw new Error('Supabase URL is not configured');
  }
};

/**
 * Gets Supabase anon key - works in both main app and widget contexts
 */
export const getSupabaseKey = (): string => {
  // Try environment variable first
  const envKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (envKey) return envKey;

  // Try window global (for runtime injection in widgets)
  if (typeof window !== 'undefined' && (window as any).__INTENTDRIVE_SUPABASE_ANON_KEY__) {
    return (window as any).__INTENTDRIVE_SUPABASE_ANON_KEY__;
  }

  // Fallback to client instance (for main app)
  try {
    const { supabase } = require('@/integrations/supabase/client');
    return supabase.supabaseKey;
  } catch {
    throw new Error('Supabase anon key is not configured');
  }
};

/**
 * Get the URL for Supabase Edge Functions
 */
export const getEdgeFunctionUrl = (functionName: string): string => {
  return `${getSupabaseUrl()}/functions/v1/${functionName}`;
};

/**
 * Check if a UUID is valid
 * @param uuid - UUID to validate
 * @returns Whether the UUID is valid
 */
export const isValidUUID = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

/**
 * Format a Supabase error message for display
 * @param error - Supabase error
 * @returns Formatted error message
 */
export const formatSupabaseError = (error: unknown): string => {
  if (!error) return 'Unknown error';

  // Type guard to check if error has the expected properties
  const isErrorWithCodeAndMessage = (err: unknown): err is { code?: string; message?: string } => {
    return typeof err === 'object' && err !== null;
  };

  if (!isErrorWithCodeAndMessage(error)) {
    return 'An unexpected error occurred';
  }

  // Handle PostgreSQL errors
  if (error.code && error.code.startsWith('P')) {
    switch (error.code) {
      case 'P0001':
        return 'Database constraint violation: ' + (error.message || 'Unknown error');
      case 'P0002':
        return 'No data found';
      case 'P0003':
        return 'Too many results returned';
      default:
        return `Database error (${error.code}): ${error.message || 'Unknown error'}`;
    }
  }

  // Handle authentication errors
  if (error.message && error.message.includes('auth')) {
    if (error.message.includes('email')) {
      return 'Invalid email or password';
    }
    if (error.message.includes('password')) {
      return 'Invalid password';
    }
    return 'Authentication error: ' + error.message;
  }

  // Handle network errors
  if (error.message && error.message.includes('network')) {
    return 'Network error: Please check your internet connection';
  }

  // Default error message
  return error.message || 'An unexpected error occurred';
};
